// Reusable "Notifications & Sound" toggle card.
//
// Lives on landlord SettingsPage AND tenant/SP ProfilePage so every role
// has a way to enable Web Push without us needing to add a Settings link
// to the gear menu for non-landlord roles.
//
// Behaviour:
//   - Hides itself entirely if the browser doesn't support Web Push
//     (very old Android, some embedded webviews, etc).
//   - Toggle state mirrors whether THIS device has an active subscription
//     registered server-side (NOT a global "user has push on" — push is
//     per-device by design, since every browser issues its own endpoint).
//   - Permission denied gets a clear hint pointing the user to browser
//     site-settings (since the browser will silently ignore future
//     requestPermission() calls once they've blocked once).

// Compact single-row toggle that matches the existing gear-dropdown menu
// rows (label on left, action affordance on right). Shows a tiny inline
// hint below when notifications are blocked at the OS/browser level.

import React, { useEffect, useState, useContext } from "react";
import Ctx from "../../contexts/AppContext";
import { rad } from "../../constants/styles";
import {
    isPushSupported,
    getNotificationPermission,
    enablePushNotifications,
    disablePushNotifications,
    isPushEnabledHere,
} from "../../utils/pushSubscribe";

export default function PushToggle() {
    const { t, h, flash } = useContext(Ctx);
    const [on, setOn] = useState(false);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => {
        let mounted = true;
        if (!isPushSupported()) return;
        isPushEnabledHere().then(v => { if (mounted) setOn(v); });
        return () => { mounted = false; };
    }, []);

    if (!isPushSupported()) return null;

    const toggle = async (e) => {
        e?.stopPropagation?.();
        if (busy) return;
        setErr("");
        setBusy(true);
        try {
            if (on) {
                await disablePushNotifications();
                setOn(false);
                flash && flash(t.notifTurnedOff || "Notifications off");
            } else {
                const r = await enablePushNotifications();
                if (r.ok) {
                    setOn(true);
                    flash && flash(t.notifEnabled || "Notifications enabled");
                } else if (r.reason === "denied") {
                    setErr(t.notifDeniedHint || "Blocked. Allow in browser settings.");
                } else if (r.reason === "unsupported") {
                    setErr(t.notifUnsupported || "Not supported on this browser.");
                } else if (r.reason === "no-vapid-key") {
                    setErr(t.notifServerMissing || "Server not configured yet.");
                } else {
                    setErr(`Failed (${r.reason || "unknown"})`);
                }
            }
        } finally {
            setBusy(false);
        }
    };

    return (
        <div onClick={(e) => e.stopPropagation()}>
            <div style={{
                padding: "10px 12px", borderRadius: rad.sm, display: "flex",
                justifyContent: "space-between", alignItems: "center",
                fontSize: 13, color: h.tx
            }}>
                <span style={{ fontWeight: 500 }}>
                    {"\uD83D\uDD14 "}{t.notifSoundTitle || "Notifications"}
                </span>
                <button
                    onClick={toggle}
                    disabled={busy}
                    aria-pressed={on}
                    style={{
                        width: 38, height: 22, borderRadius: 11,
                        background: on ? (h.gn || "#16a34a") : `${h.bd}`,
                        border: "none", cursor: busy ? "not-allowed" : "pointer",
                        position: "relative", padding: 0, opacity: busy ? 0.6 : 1,
                        transition: "background 0.2s", flexShrink: 0
                    }}>
                    <span style={{
                        position: "absolute", top: 2, left: on ? 18 : 2,
                        width: 18, height: 18, borderRadius: 9, background: "#fff",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.25)", transition: "left 0.2s"
                    }}/>
                </button>
            </div>
            {/* Only show error after the user has actually tapped the toggle
                and hit a problem. Don't auto-display a "blocked" hint on
                every dropdown open — that clutters the menu for users who
                simply chose not to enable notifications. */}
            {err && (
                <div style={{
                    color: h.rd, fontSize: 10,
                    padding: "0 12px 6px", lineHeight: 1.3
                }}>
                    {err}
                </div>
            )}
        </div>
    );
}
